import React, { Component } from "react";
import { Form, Switch, Tooltip, Image, Row, Col, Input } from "antd";
import { EyeOutlined, UpOutlined, WarningOutlined, InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";

const ACENTO = "#00BFD6";
// Alto del recuadro de la captura. Fijo para que todas midan igual, sin
// importar el tamaño del PNG: la imagen se ajusta dentro con `contain`.
const ALTO_CAPTURA = 130;

// Iguala el label de los FormItem dependientes al nombre de la opción. Se hace
// por clase y no con un label como nodo, porque el mensaje de `required` de
// FormItem necesita que `label` siga siendo un string.
if (typeof document !== "undefined") {
    const styleId = "config-dependientes-style";
    if (!document.getElementById(styleId)) {
        const styleEl = document.createElement("style");
        styleEl.id = styleId;
        styleEl.textContent = `
            .config-dependientes .ant-form-item-label > label {
                font-size: 14px;
                font-weight: 500;
                color: #4a4a4a;
                height: auto;
            }
        `;
        document.head.appendChild(styleEl);
    }
}

/**
 * Renglón de una opción de configuración: nombre a la izquierda, switch a la
 * derecha, el resumen en el tooltip del ⓘ y el resto en "Ver detalle".
 *
 * Props:
 *  - name         nombre del campo en el Form
 *  - titulo       nombre corto de la opción
 *  - resumen      una línea para el tooltip; texto plano (el fondo es oscuro)
 *  - detalle      texto largo del panel. Acepta nodo: ver RefBoton / RefModulo
 *  - maqueta      mini-maqueta de "qué cambia" (ver ConfigMock)
 *  - imagen       captura(s): una ruta, una lista de rutas o [{ src, titulo }].
 *                 Todas se dibujan en un recuadro del mismo alto; la que no
 *                 exista se omite sola.
 *  - aviso        advertencia corta (icono ámbar + tooltip)
 *  - disabled     igual que en Switch
 *  - afterChange  callback(checked). No se llama `onChange` porque Form.Item
 *                 sobreescribiría esa prop
 *  - ultimo       quita el separador inferior
 */
// Normaliza `imagen` a una lista de { src, titulo }.
const listaImagenes = (imagen) => {
    if (!imagen) return [];
    return (Array.isArray(imagen) ? imagen : [imagen])
        .map((img) => (typeof img === "string" ? { src: img } : img))
        .filter((img) => img && img.src);
};

class Renglon extends Component {
    state = { abierto: false, hover: false, fallidas: [] };

    setChecked = (valor) => {
        const { onChange, afterChange, disabled } = this.props;
        if (disabled) return;
        if (onChange) onChange(valor);
        if (afterChange) afterChange(valor);
    };

    render() {
        const {
            checked,
            titulo,
            resumen,
            detalle,
            maqueta,
            imagen,
            aviso,
            disabled,
            ultimo,
            oculto,
        } = this.props;
        const { abierto, hover, fallidas } = this.state;

        const activo = !!checked;
        const imagenes = listaImagenes(imagen).filter((img) => !fallidas.includes(img.src));
        const hayImagen = imagenes.length > 0;
        const hayMaqueta = !!(maqueta || hayImagen);
        const expandible = hayMaqueta || !!detalle;
        const ayuda = resumen || detalle;

        return (
            <div
                onMouseEnter={() => this.setState({ hover: true })}
                onMouseLeave={() => this.setState({ hover: false })}
                style={{
                    display: oculto ? "none" : undefined,
                    borderBottom: ultimo ? "none" : "1px solid #f5f5f5",
                    background: hover ? "#fafafa" : "transparent",
                    padding: "4px 6px",
                    transition: "background 0.15s ease",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {/* Todo el nombre alterna el switch, y crece para que todos
                        los switches queden alineados en la misma vertical. */}
                    <div
                        onClick={() => this.setChecked(!activo)}
                        style={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            cursor: disabled ? "default" : "pointer",
                            userSelect: "none",
                        }}
                    >
                        <span
                            style={{
                                fontSize: 14,
                                fontWeight: 500,
                                lineHeight: 1.35,
                                color: disabled ? "#b0b0b0" : "#4a4a4a",
                            }}
                        >
                            {titulo}
                        </span>
                        {ayuda && (
                            <Tooltip title={ayuda}>
                                <InfoCircleOutlined
                                    style={{ color: ACENTO, fontSize: 12, flexShrink: 0 }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </Tooltip>
                        )}
                        {aviso && (
                            <Tooltip title={aviso}>
                                <WarningOutlined
                                    style={{ color: "#faad14", fontSize: 12, flexShrink: 0 }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </Tooltip>
                        )}
                    </div>

                    <Switch
                        size="small"
                        checked={activo}
                        disabled={disabled}
                        onChange={(valor) => this.setChecked(valor)}
                        style={{ flexShrink: 0 }}
                    />
                </div>

                {expandible && (
                    <span
                        onClick={() => this.setState({ abierto: !abierto })}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: 12,
                            lineHeight: 1.2,
                            color: "#b8b8b8",
                            cursor: "pointer",
                            userSelect: "none",
                        }}
                    >
                        {abierto ? <UpOutlined style={{ fontSize: 10 }} /> : <EyeOutlined style={{ fontSize: 11 }} />}
                        {abierto ? "Ocultar" : "Ver detalle"}
                    </span>
                )}

                {abierto && (
                    <div
                        style={{
                            marginTop: 4,
                            marginBottom: 2,
                            padding: 8,
                            background: "#fbfdfe",
                            border: "1px solid #e9f5f8",
                            borderRadius: 5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 7,
                        }}
                    >
                        {maqueta}

                        {hayImagen && (
                            <Image.PreviewGroup>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                    {imagenes.map((img) => (
                                        <figure
                                            key={img.src}
                                            style={{
                                                margin: 0,
                                                flex: imagenes.length > 1 ? "1 1 180px" : "1 1 100%",
                                                minWidth: 0,
                                            }}
                                        >
                                            <Image
                                                src={img.src}
                                                alt={img.titulo || titulo}
                                                loading="lazy"
                                                onError={() =>
                                                    this.setState((s) => ({
                                                        fallidas: [...s.fallidas, img.src],
                                                    }))
                                                }
                                                wrapperStyle={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "100%",
                                                    height: ALTO_CAPTURA,
                                                    padding: 4,
                                                    background: "#fff",
                                                    borderRadius: 4,
                                                    border: "1px solid #e8e8e8",
                                                    overflow: "hidden",
                                                }}
                                                style={{
                                                    maxWidth: "100%",
                                                    maxHeight: "100%",
                                                    objectFit: "contain",
                                                }}
                                                preview={{ mask: "Ampliar" }}
                                            />
                                            {img.titulo && (
                                                <figcaption
                                                    style={{
                                                        fontSize: 11,
                                                        color: "#a0a0a0",
                                                        marginTop: 3,
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    {img.titulo}
                                                </figcaption>
                                            )}
                                        </figure>
                                    ))}
                                </div>
                            </Image.PreviewGroup>
                        )}

                        {detalle && (
                            <div style={{ fontSize: 12.5, lineHeight: 1.5, color: "#8c8c8c" }}>
                                {detalle}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

/**
 * Sangría y regla vertical para los campos que dependen de la opción de arriba.
 * No decide nada: la condición la sigue controlando quien la usa con `shouldUpdate`.
 */
export const ConfigDependientes = ({ titulo, children }) => (
    <div
        className="config-dependientes"
        style={{
            margin: "0 6px 8px 20px",
            paddingLeft: 10,
            borderLeft: `2px solid ${ACENTO}40`,
        }}
    >
        {titulo && (
            <div
                style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    color: "#b8b8b8",
                    margin: "6px 0 3px",
                }}
            >
                {titulo}
            </div>
        )}
        {children}
    </div>
);

// Módulos que cobra BIWO: { bloqueados: [claves], dependientes: { subClave: clavePadre } }
export const ModulosBiwoContext = React.createContext({ bloqueados: [], dependientes: {} });

export const bloqueoModuloBiwo = ({ bloqueados, dependientes }, name, getFieldValue) => {
    if (bloqueados.includes(name)) return "Módulo contratado: para darlo de baja pídelo a BIWO";
    const padre = dependientes[name];
    if (padre && !getFieldValue(padre)) return "Disponible al contratar el módulo principal con BIWO";
    return null;
};

/** Conecta el renglón al Form. `valuePropName="checked"` como en los switches previos. */
const ConfigSwitch = ({ name, initialValue, ...resto }) => (
    <ModulosBiwoContext.Consumer>
        {(modulosBiwo) => (
            <Form.Item noStyle shouldUpdate={(a, b) => !modulosBiwo.dependientes[name] || a[modulosBiwo.dependientes[name]] !== b[modulosBiwo.dependientes[name]]}>
                {({ getFieldValue }) => {
                    const bloqueo = bloqueoModuloBiwo(modulosBiwo, name, getFieldValue);
                    return (
                        <Form.Item name={name} valuePropName="checked" initialValue={initialValue} noStyle>
                            <Renglon {...resto} disabled={resto.disabled || !!bloqueo} aviso={bloqueo || resto.aviso} />
                        </Form.Item>
                    );
                }}
            </Form.Item>
        )}
    </ModulosBiwoContext.Consumer>
);

export default ConfigSwitch;

const normalizar = (str) =>
    String(str || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");

// `resumen`, `detalle` y `aviso` pueden venir como nodo: se aplanan a texto.
const textoPlano = (nodo) => {
    if (nodo === null || nodo === undefined || typeof nodo === "boolean") return "";
    if (typeof nodo === "string" || typeof nodo === "number") return String(nodo);
    if (Array.isArray(nodo)) return nodo.map(textoPlano).join(" ");
    if (React.isValidElement(nodo)) return textoPlano(nodo.props?.children);
    return "";
};

const textoBuscable = (props) =>
    [props.name, props.titulo, props.resumen, props.detalle, props.aviso]
        .map((x) => normalizar(textoPlano(x)))
        .join(" ");

// Se listan explícitamente para no recorrer nodos cuyos children son una
// función (los render-prop de `FormItem shouldUpdate`).
const esContenedor = (nodo) =>
    nodo.type === Row || nodo.type === Col || nodo.type === React.Fragment;

// Devuelve [hijos, cuántos ConfigSwitch quedaron visibles].
const filtrarHijos = (children, termino) => {
    let visibles = 0;
    // Un nodo que no es ConfigSwitch depende del switch inmediatamente
    // anterior; antes del primero se asume visible.
    let ultimaVisible = true;

    const salida = React.Children.map(children, (nodo) => {
        if (!React.isValidElement(nodo)) return nodo;

        if (esContenedor(nodo)) {
            const [hijos, n] = filtrarHijos(nodo.props.children, termino);
            visibles += n;
            return React.cloneElement(nodo, {}, hijos);
        }

        if (nodo.type === ConfigSwitch) {
            const coincide = textoBuscable(nodo.props).includes(termino);
            ultimaVisible = coincide;
            if (coincide) visibles += 1;
            return React.cloneElement(nodo, { oculto: !coincide });
        }

        return <div style={ultimaVisible ? undefined : { display: "none" }}>{nodo}</div>;
    });

    return [salida, visibles];
};

/**
 * Buscador de la sección: trae su propio input y su propio estado, así que
 * basta con envolver el <Row> de la sección. Busca en título, resumen, detalle,
 * aviso y `name`, sin distinguir mayúsculas ni acentos.
 *
 * Oculta con `display: none` en lugar de sacar el renglón del árbol, para que
 * los Form.Item sigan montados y no se desregistren sus reglas de validación.
 *
 * Props:
 *  - placeholder  texto del input
 *  - vacio        nodo a mostrar cuando nada coincide
 */
export class ConfigFiltro extends Component {
    state = { termino: "" };

    render() {
        const { children, placeholder, vacio } = this.props;
        const { termino } = this.state;
        const buscado = normalizar(termino.trim());

        const [filtrados, visibles] = buscado
            ? filtrarHijos(children, buscado)
            : [children, -1];

        return (
            <>
                <div style={{ marginBottom: 25, maxWidth: 320 }}>
                    <Input
                        allowClear
                        size="small"
                        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                        placeholder={placeholder || "Buscar opción..."}
                        value={termino}
                        onChange={(e) => this.setState({ termino: e.target.value })}
                        style={{ borderRadius: 6 }}
                    />
                </div>

                {visibles === 0 &&
                    (vacio || (
                        <div style={{ padding: "14px 4px", fontSize: 13, color: "#a8a8a8" }}>
                            No hay opciones que coincidan con “{termino.trim()}”.
                        </div>
                    ))}
                {filtrados}
            </>
        );
    }
}
